import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils, comnEnvs } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page, Tab, Tree } from "@/comn/components";
import { useFetch, useForm, useModal, useToast, useTab, useResource, useTree, usePage, useGrid } from "@/comn/hooks";
import {
    APIS,
    SF_ORG_USR_INFO,
    URLS,
    SG_USR_AUTH_LIST,
    FIXED_USR_AUTH_IDS,
    // [2026-10-30] One Stop Centre 소속 사용자 권한 제한용
    OSC_ORG_CD,
    OSC_ORG_ONLY_AUTH_ID,
} from "@/tz/go/egaz/osci/ptl/usr/orgusr/services/OsciOrgUsrService";
import { OSCI0103018Q } from "./OSCI0103018Q";

/*
 * Modifying and Deleting Organization User Management
 * !== 기관 관리 수정 및 삭제 ==!
 */
export const OSCI0103012S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { usrIdntfId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!

    useResource({
        defaultSchema: [{ area: "orgDeptCd" }, { area: "orgCd" }, { area: "cntyCd" }, { area: "cstmCd" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Organization User Information Form !== 기관 사용자 정보 form ==!
        orgUsrInfo: useForm({
            defaultSchema: SF_ORG_USR_INFO,
            defaultValues: {},
        }),
    };

    const grid = {
        ogaUsrAuthList: useGrid({ defaultSchema: SG_USR_AUTH_LIST }),
    };

    const orgCd = form.orgUsrInfo.watch("orgCd");

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Check Organization User Information !== 기관 사용자 정보 조회 ==!
        getOrgUsrInfo: useFetch({
            api: (data) => APIS.getOrgUsrInfo(usrIdntfId),
            enabled: !!usrIdntfId,
            onSuccess: (data) => {
                const orgUsrInfo = data.orgUsrInfo.content;
                form.orgUsrInfo.setValues(orgUsrInfo);
                form.orgUsrInfo.setValue("usrPwd", "");
                fetch.getOgaUsrAuthList.fetch();
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00035" }), // An error occurred. !== 에러가 발생했습니다! ==!
        }),

        getOgaUsrAuthList: useFetch({
            api: (page = grid.ogaUsrAuthList.page, size = grid.ogaUsrAuthList.size) =>
                APIS.getOgaUsrAuthList({ usrIdntfId: usrIdntfId }, page, size),
            onSuccess: (data) => {
                const ogaUsrAuthList = data.authList.content;
                if (ogaUsrAuthList != null) grid.ogaUsrAuthList.setData(comnUtils.getGridData(ogaUsrAuthList));
            },
            onError: () => modal.openModal({ content: "msg.osc.00043" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),

        // Update Organization User Information !== 기관 사용자 정보 수정 ==!
        updateOrgInfo: useFetch({
            api: (info: any) => APIS.updateOrgUsrInfo(info),
            enabled: false,
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00073", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => navigate(`${URLS.osci0103010q}?menuId=${menuId}`),
                    close: false,
                });
            },
            onError: (err) => {
                toast.showToast({ type: "warning", content: err.response.data.errorMessage }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
            },
        }),
    };

    /*
     * Declaration Event Render
     * !== 랜더 정의 ==!
     */
    const render = {
        // 시스템이 관리하는 권한은 체크박스를 막아 삭제할 수 없도록 한다
        checkbox: (row: any) => !FIXED_USR_AUTH_IDS.includes(row.authId),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click List Button !== 목록 버튼 클릭 ==!
        onListBtnClick: () => navigate(`${URLS.osci0103010q}?menuId=${menuId}`),
        // Click Update Button !== 수정 버튼 클릭 ==!
        onUpdateBtnClick: form.orgUsrInfo.handleSubmit(
            () => {
                // if (CSTM_OFCE_CD_RQRD_ARR.includes(form.orgUsrInfo.getValue("orgCd"))) {
                //     if (comnUtils.isEmpty(form.orgUsrInfo.getValue("cstmOfceCd"))) {
                //         form.orgUsrInfo.setError("cstmOfceCd", {
                //             message: "msg.osc.00013", // Mandatory fields !== 필수 입력 필드 ==!
                //             type: "error",
                //         });
                //         return;
                //     }
                // }

                const data = form.orgUsrInfo.getValues();
                const ogaUsrAuthList = grid.ogaUsrAuthList.getData();
                const dataResult = { ...data, ogaUsrAuthList: ogaUsrAuthList };

                modal.openModal({
                    content: "msg.osc.00064", // Confirm update? !== 수정하시겠습니까? ==!
                    onConfirm: () => fetch.updateOrgInfo.fetch(dataResult),
                });
            },
            () => toast.showToast({ type: "warning", content: "msg.osc.00003" }), // Data is invalid, !== 데이터가 유효하지않습니다. ==!
        ),
        onUsrStatChange: (value: any) => {
            if (value == 2) {
                form.orgUsrInfo.setSchema("mdfyRsn", { required: true });
            } else {
                form.orgUsrInfo.setSchema("mdfyRsn", { required: false });
            }
        },

        onBtnGrdAddRowClick: () => {
            const data = grid.ogaUsrAuthList.getData({ excludes: ["origin"] }); // INSERT, DELETE

            if (!comnUtils.isEmpty(data)) {
                modal.openModal({ content: "msg.osc.00219" }); // Data has been changed. Please save it and proceed. !== 데이터가 변경되었습니다. 저장 후 진행해 주세요. ==!
                return;
            }

            /*
             * [2026-10-30] 조회된 사용자의 기관이 One Stop Centre(OGAZ0037) 면
             * 관리자 권한(auth_osci0003) 만 선택할 수 있도록 팝업 조회를 고정한다.
             * 그 외 기관은 undefined 를 넘겨 기존과 동일하게 관리자 권한을 제외한 목록을 본다.
             */
            const fixedAuthId = orgCd === OSC_ORG_CD ? OSC_ORG_ONLY_AUTH_ID : undefined;

            const modalCallback = (list: any) => {
                const authList = grid.ogaUsrAuthList.getData();
                list.forEach(
                    (item: any) =>
                        authList.findIndex((subItem: any) => item.authId === subItem.authId) === -1 &&
                        grid.ogaUsrAuthList.addRow(item),
                );
                modal.closeModal();
            };

            modal.openModal({
                draggable: true,
                size: "sm",
                content: (
                    <OSCI0103018Q
                        psnTin={form.orgUsrInfo.getValue("psnTin")}
                        fixedAuthId={fixedAuthId}
                        ogaUsrAuthList={grid.ogaUsrAuthList.getData()}
                        callback={modalCallback}
                    />
                ),
            });
        },

        onBtnGrdDelRowClick: () => {
            if (comnUtils.isEmpty(grid.ogaUsrAuthList.getChecked())) {
                modal.openModal({ content: "msg.osc.00084" }); // No data has been selected.. !== 선택된 데이터가 없습니다. ==!
                return;
            }

            grid.ogaUsrAuthList.deleteRow("checkbox");
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getOrgUsrInfo.fetch();
        form.orgUsrInfo.setSchema("psnTin", { edit: false });
        form.orgUsrInfo.setSchema("orgCd", { edit: false });
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.psnTin} />
                            <Group.Control {...form.orgUsrInfo.schema.usrNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.orgCd} />
                            <Group.Control
                                {...form.orgUsrInfo.schema.deptCd}
                                popupParams={{ orgCd: orgCd }}
                                filter={(_option: any, division: any) => division?.orgCd === orgCd}
                            />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.orgRppnYn} />
                            {/* <Group.Control {...form.orgUsrInfo.schema.frgnrYn} /> */}
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.aplcCntyCd} />
                            <Group.Control {...form.orgUsrInfo.schema.usrMoblNo} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.usrEmlAddr} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.rsdtAddr} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.usrStatCd} onChange={handler.onUsrStatChange} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.mdfyRsn} />
                        </Group.Row>
                        {/* <Group.Row> */}
                        {/* <Group.Control {...form.orgUsrInfo.schema.cstmOfceCd} /> */}
                        {/* <Group.Cell size={6}>{t("msg.osc.00249")}</Group.Cell> */}
                        {/* </Group.Row> */}
                    </Group.Section>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Title title={"T_OGA_USR_AUTH_LIST"} />
                    <Group.Section gap={2}>
                        <Layout.Right>
                            <Button role={"gridAdd"} onClick={handler.onBtnGrdAddRowClick} />
                            <Button role={"gridDelete"} onClick={handler.onBtnGrdDelRowClick} />
                        </Layout.Right>

                        <Grid {...grid.ogaUsrAuthList.grid} render={render} />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Layout.Button role={"list"} onClick={handler.onListBtnClick} />
                </Layout.Left>
                <Layout.Right>
                    <Layout.Button role={"save"} onClick={handler.onUpdateBtnClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
